<template>
  <div class="shortcut-dashboard-page">
    <!-- Header / Action Bar -->
    <div class="shortcut-header-card mb-6">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between">
        <!-- Title & Subtitle -->
        <div class="header-left mb-4 md:mb-0">
          <div class="flex items-center">
            <div class="header-icon-box mr-3 flex items-center justify-center rounded-lg">
              <feather-icon icon="GridIcon" svgClasses="h-6 w-6 text-primary" />
            </div>
            <div>
              <div class="flex items-center">
                <h3 class="font-bold text-xl header-title leading-tight mr-2">Shortcut Dashboard</h3>
                <span class="shortcut-badge text-xs px-2.5 py-0.5 rounded-full font-medium" v-if="total_visible_count">
                  {{ total_visible_count }} {{ total_visible_count === 1 ? 'item' : 'items' }}
                </span>
              </div>
              <p class="header-subtitle text-xs mt-0.5">
                Quick access to your most frequently used modules and reports
              </p>
            </div>
          </div>
        </div>

        <!-- Right Controls: Search, Manage, Add -->
        <div class="header-right flex items-center flex-wrap gap-2">
          <!-- Quick Search -->
          <div class="search-box-wrapper mr-2" v-if="formatted_shortcut_list.length > 3">
            <vs-input
              icon-pack="feather"
              icon="icon-search"
              icon-after
              placeholder="Search shortcuts..."
              v-model="search_query"
              class="shortcut-search-input"
            />
          </div>

          <!-- Manage / Done Button -->
          <vs-button
            v-if="formatted_shortcut_list.length > 0"
            :color="is_on_edit_mode ? 'warning' : 'primary'"
            :type="is_on_edit_mode ? 'filled' : 'border'"
            :icon="is_on_edit_mode ? 'icon-check' : 'icon-settings'"
            icon-pack="feather"
            class="mr-2"
            @click.stop="onClickManageShortcut"
          >
            {{ is_on_edit_mode ? 'Done Editing' : 'Manage' }}
          </vs-button>

          <!-- Add Shortcut Button -->
          <vs-button
            color="primary"
            type="filled"
            icon="icon-plus"
            icon-pack="feather"
            @click.stop="onClickAddShortCut"
          >
            Add Shortcut
          </vs-button>
        </div>
      </div>

      <!-- Edit Mode Banner Hint -->
      <transition name="fade">
        <div v-if="is_on_edit_mode" class="edit-mode-banner mt-4 p-3 rounded-lg flex items-center justify-between">
          <div class="flex items-center text-sm">
            <feather-icon icon="InfoIcon" svgClasses="h-4 w-4 mr-2 text-warning" />
            <span><strong>Edit Mode:</strong> Click the red trash icon on any shortcut to delete it from your dashboard.</span>
          </div>
          <vs-button size="small" color="warning" type="flat" @click="is_on_edit_mode = false">Done</vs-button>
        </div>
      </transition>
    </div>

    <!-- Full-Width Weekly Schedule Timeline Row -->
    <div class="vx-row mb-6" v-if="weekly_schedule_shortcut">
      <div class="vx-col w-full">
        <div
          class="shortcut-card-container relative h-full"
          :class="{
            'edit-mode-active': is_on_edit_mode,
            'is-deleting': deleting_shortcut_id === weekly_schedule_shortcut.unique_id
          }"
        >
          <!-- Floating Remove Button in Edit Mode -->
          <transition name="pop">
            <div class="shortcut-delete-btn-wrapper" v-if="is_on_edit_mode">
              <vx-tooltip text="Delete Shortcut" position="top">
                <button
                  type="button"
                  class="shortcut-remove-btn flex items-center justify-center"
                  :disabled="deleting_shortcut_id === weekly_schedule_shortcut.unique_id"
                  @click.stop="onClickRemove(weekly_schedule_shortcut.unique_id)"
                  title="Remove"
                >
                  <feather-icon
                    :icon="deleting_shortcut_id === weekly_schedule_shortcut.unique_id ? 'LoaderIcon' : 'Trash2Icon'"
                    :svgClasses="deleting_shortcut_id === weekly_schedule_shortcut.unique_id ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'"
                  />
                </button>
              </vx-tooltip>
            </div>
          </transition>

          <weekly-schedule-timeline-card />
        </div>
      </div>
    </div>

    <!-- Shortcuts Cards Grid -->
    <div class="vx-row" id="shortcut-cards">
      <!-- Shortcut Cards List -->
      <div
        class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/3 xl:w-1/4 mb-6"
        v-for="(shortcut, index_shortcut) in filtered_shortcut_list"
        :key="shortcut.unique_id || index_shortcut"
      >
        <div
          class="shortcut-card-container relative h-full"
          :class="{
            'edit-mode-active': is_on_edit_mode,
            'is-deleting': deleting_shortcut_id === shortcut.unique_id
          }"
        >
          <!-- Floating Remove Button in Edit Mode -->
          <transition name="pop">
            <div class="shortcut-delete-btn-wrapper" v-if="is_on_edit_mode">
              <vx-tooltip text="Delete Shortcut" position="top">
                <button
                  type="button"
                  class="shortcut-remove-btn flex items-center justify-center"
                  :disabled="deleting_shortcut_id === shortcut.unique_id"
                  @click.stop="onClickRemove(shortcut.unique_id)"
                  title="Remove"
                >
                  <feather-icon
                    :icon="deleting_shortcut_id === shortcut.unique_id ? 'LoaderIcon' : 'Trash2Icon'"
                    :svgClasses="deleting_shortcut_id === shortcut.unique_id ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'"
                  />
                </button>
              </vx-tooltip>
            </div>
          </transition>

          <!-- CRM Summary Special Card -->
          <div
            v-if="shortcut.name === 'CRM Summary' && $canAny(['end-user', 'executive-admin'])"
            class="h-full"
            @click.stop="!is_on_edit_mode && onClickCrmAccountSummary()"
          >
            <crm-summary-shortcut-card
              hideChart
              statistic="CRM Summary"
              icon="BarChart2Icon"
            />
          </div>

          <!-- OTIF Special Card -->
          <div
            v-else-if="(shortcut.name === 'OTIF - Last Month' || shortcut.name === 'OTIF' || shortcut.name === 'OTIF Performance') && $canAny(['view-otif-target-setting', 'edit-otif-target-setting'])"
            class="h-full"
          >
            <otif-last-month-shortcut-card
              :statistic="shortcut.name || 'OTIF'"
              :color="shortcut.color || '#00CFE8'"
              :icon="shortcut.icon || 'TargetIcon'"
              :url="shortcut.url || '/pages/otif-report'"
              :is_edit_mode="is_on_edit_mode"
            />
          </div>

          <!-- Sales Orders Special Card -->
          <div
            v-else-if="shortcut.name === 'Sales Orders' && $canAny(['view-order'])"
            class="h-full"
            @click.stop="!is_on_edit_mode && onClickSalesOrdersShortcut()"
          >
            <sales-orders-shortcut-card
              title="Sales Orders"
              :color="shortcut.color || '#16FF00'"
              :icon="shortcut.icon || 'BoxIcon'"
              :url="shortcut.url || '/pages/product-orders'"
              :is_edit_mode="is_on_edit_mode"
            />
          </div>

          <!-- Standard Shortcut Card -->
          <div
            v-else
            class="h-full"
          >
            <shortcut-tile
              ref="tiles"
              :shortcut="shortcut"
              :index_shortcut="index_shortcut"
              :is_edit_mode="is_on_edit_mode"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State: No Shortcuts at all -->
    <div v-if="formatted_shortcut_list.length === 0" class="empty-state-card text-center p-12 rounded-xl mt-4">
      <div class="empty-icon-circle mx-auto mb-4 flex items-center justify-center rounded-full">
        <feather-icon icon="GridIcon" svgClasses="h-10 w-10 text-primary" />
      </div>
      <h3 class="font-bold text-xl mb-2 empty-title">No Shortcuts Added Yet</h3>
      <p class="empty-desc text-sm max-w-md mx-auto mb-6">
        Personalize your workspace! Add quick shortcuts to your most important modules, formulations, reports, and CRM tools.
      </p>
      <vs-button
        color="primary"
        type="filled"
        icon="icon-plus"
        icon-pack="feather"
        size="large"
        @click.stop="onClickAddShortCut"
      >
        Create Your First Shortcut
      </vs-button>
    </div>

    <!-- Empty State: Search Filter has no results -->
    <div v-else-if="filtered_shortcut_list.length === 0 && !weekly_schedule_shortcut" class="empty-state-card text-center p-8 rounded-xl mt-4">
      <div class="empty-icon-circle mx-auto mb-3 flex items-center justify-center rounded-full">
        <feather-icon icon="SearchIcon" svgClasses="h-8 w-8 text-primary" />
      </div>
      <h4 class="font-bold text-lg mb-1 empty-title">No matching shortcuts found</h4>
      <p class="empty-desc text-xs mb-4">
        No shortcuts match your search "<strong>{{ search_query }}</strong>".
      </p>
      <vs-button color="primary" type="border" size="small" @click="search_query = ''">
        Clear Search
      </vs-button>
    </div>

    <!-- Create Shortcut Dialog PopUp -->
    <create-shortcut-pop-up ref="createShortcutPopUp" />
  </div>
</template>

<script>
import ShortcutTile from './components/ShortcutTile.vue'
import CreateShortcutPopUp from '@/components/popups/shortcuts/CreateShortcutPopUp.vue'
import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'
import { shortcut_list } from '@/constants/shortcuts'

import { RepositoryFactory } from '@/repository/RepositoryFactory'
const ShortcutsRepository = RepositoryFactory.get('shortcuts')
import CrmSummaryShortcutCard from '@/components/shortcut-cards/CrmSummaryShortcutCard.vue'
import OtifLastMonthShortcutCard from '@/components/shortcut-cards/OtifLastMonthShortcutCard.vue'
import WeeklyScheduleTimelineCard from '@/components/shortcut-cards/WeeklyScheduleTimelineCard.vue'
import SalesOrdersShortcutCard from '@/components/shortcut-cards/SalesOrdersShortcutCard.vue'

export default {
  name: "ShortcutDashboard",
  data() {
    return {
      shortcut_list: shortcut_list,
      formatted_shortcut_list: [],
      is_on_edit_mode: false,
      is_crm_summary_enable: false,
      search_query: '',
      deleting_shortcut_id: null
    }
  },
  components: {
    ShortcutTile,
    CreateShortcutPopUp,
    CrmSummaryShortcutCard,
    OtifLastMonthShortcutCard,
    WeeklyScheduleTimelineCard,
    SalesOrdersShortcutCard,
  },
  mixins: [SuccessMessage, ErrorMessages],
  computed: {
    total_visible_count() {
      return this.filtered_shortcut_list.length + (this.weekly_schedule_shortcut ? 1 : 0)
    },
    weekly_schedule_shortcut() {
      const shortcut = this.formatted_shortcut_list.find(s => s.name === 'Weekly Schedule' && this.hasPermisions(s.permissions))
      if (!shortcut) return null
      if (!this.search_query.trim()) return shortcut
      const q = this.search_query.toLowerCase().trim()
      const match = (shortcut.name && shortcut.name.toLowerCase().includes(q)) || (shortcut.description && shortcut.description.toLowerCase().includes(q))
      return match ? shortcut : null
    },
    filtered_shortcut_list() {
      let list = this.formatted_shortcut_list.filter(shortcut => shortcut.name !== 'Weekly Schedule' && this.hasPermisions(shortcut.permissions));
      if (!this.search_query.trim()) {
        return list;
      }
      const q = this.search_query.toLowerCase().trim();
      return list.filter(item => {
        const nameMatch = item.name && item.name.toLowerCase().includes(q);
        const descMatch = item.description && item.description.toLowerCase().includes(q);
        return nameMatch || descMatch;
      });
    }
  },
  methods: {
    async refreshData() {
      await this.fetchShortcutList()
    },
    async onClickAddShortCut() {
      await this.$refs.createShortcutPopUp.openPopup(this.formatted_shortcut_list)
    },
    onClickManageShortcut() {
      this.is_on_edit_mode = !this.is_on_edit_mode
    },
    async fetchShortcutList() {
      try {
        const { data } = (await ShortcutsRepository.getListOfShortcutList()).data
        const list = []
        data.forEach((x) => {
          let find = this.shortcut_list.find((y) => y.id === x.shortcut_id)
          if (find) {
            const shortcutCopy = { ...find }
            shortcutCopy.color = x.color || find.color
            shortcutCopy.icon = x.icon || find.icon
            shortcutCopy.unique_id = x.id
            list.push(shortcutCopy)
          }
        })
        this.formatted_shortcut_list = list
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    async onClickRemove(id) {
      this.deleting_shortcut_id = id
      try {
        await ShortcutsRepository.deleteShortcut(id)
        await this.fetchShortcutList()
        this.showSuccessMessage('Shortcut removed successfully')
        if (this.formatted_shortcut_list.length === 0) {
          this.is_on_edit_mode = false
        }
      } catch (error) {
        this.convertAndNotifyError(error)
      } finally {
        this.deleting_shortcut_id = null
        this.$vs.loading.close()
        this.$vs.loading.close('#shortcut-cards > .con-vs-loading')
        // Clean up any remaining loading overlays just in case
        document.querySelectorAll('.con-vs-loading').forEach(el => el.remove())
      }
    },
    hasPermisions(permissions = []) {
      return permissions.length ? this.$canAny(permissions) : true;
    },
    onClickCrmAccountSummary() {
      this.$router.push(`/pages/crm/account-owner-summary`).catch(() => { });
    },
    onClickOtifShortcut(shortcut) {
      const targetUrl = (shortcut && shortcut.url) ? shortcut.url : '/pages/otif-report'
      this.$router.push(targetUrl).catch(() => { });
    },
    onClickSalesOrdersShortcut() {
      this.$router.push(`/pages/product-orders`).catch(() => { });
    }
  },
  async mounted() {
    await this.fetchShortcutList()
  },
}
</script>

<style lang="scss" scoped>
.shortcut-dashboard-page {
  padding-bottom: 2rem;
}

/* Header Card */
.shortcut-header-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 18px 0 rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;

  .header-icon-box {
    width: 44px;
    height: 44px;
    background: rgba(var(--vs-primary), 0.1);
    color: rgba(var(--vs-primary), 1);
  }

  .header-title {
    color: #2c2c2c;
  }

  .header-subtitle {
    color: #727b87;
  }

  .shortcut-badge {
    background: rgba(var(--vs-primary), 0.1);
    color: rgba(var(--vs-primary), 1);
  }
}

/* Edit Mode Banner */
.edit-mode-banner {
  background: rgba(var(--vs-warning), 0.12);
  border: 1px solid rgba(var(--vs-warning), 0.35);
  color: #8c6200;
}

/* Card Container & Delete Button */
.shortcut-card-container {
  transition: transform 0.2s ease;

  &.edit-mode-active {
    animation: gentle-pulse 2s infinite ease-in-out;
  }
}

.shortcut-delete-btn-wrapper {
  position: absolute;
  top: -7px;
  right: -7px;
  margin: 2px;
  z-index: 20;
}

.shortcut-remove-btn {
  width: 30px;
  height: 30px;
  padding: 6px;
  border-radius: 50%;
  background: #ea5455;
  color: #ffffff;
  border: 2px solid #ffffff;
  box-shadow: 0 3px 10px rgba(234, 84, 85, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  svg {
    width: 13px !important;
    height: 13px !important;
    margin: auto;
  }

  &:hover {
    background: #e6383a;
    transform: scale(1.15);
    box-shadow: 0 5px 14px rgba(234, 84, 85, 0.65);
  }

  &[disabled] {
    opacity: 0.85;
    cursor: not-allowed;
    transform: none !important;
  }
}

.shortcut-card-container {
  &.is-deleting {
    opacity: 0.45;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Empty State Card */
.empty-state-card {
  background: #ffffff;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  box-shadow: 0 4px 18px 0 rgba(0, 0, 0, 0.03);

  .empty-icon-circle {
    width: 72px;
    height: 72px;
    background: rgba(var(--vs-primary), 0.08);
  }

  .empty-title {
    color: #2c2c2c;
  }

  .empty-desc {
    color: #727b87;
  }
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.pop-enter-active, .pop-leave-active {
  transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.pop-enter, .pop-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

@keyframes gentle-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.99);
  }
}

/* Dark Theme Support */
::v-deep .theme-dark,
.theme-dark {
  .shortcut-header-card {
    background: #10163a;
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.35);

    .header-title {
      color: #ebeefd;
    }

    .header-subtitle {
      color: #9aa2ba;
    }

    .shortcut-badge {
      background: rgba(115, 103, 240, 0.2);
      color: #b8b0fa;
    }
  }

  .edit-mode-banner {
    background: rgba(255, 159, 67, 0.15);
    border-color: rgba(255, 159, 67, 0.4);
    color: #ffc68a;
  }

  .shortcut-remove-btn {
    border-color: #10163a;
  }

  .empty-state-card {
    background: #10163a;
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.35);

    .empty-icon-circle {
      background: rgba(115, 103, 240, 0.15);
    }

    .empty-title {
      color: #ebeefd;
    }

    .empty-desc {
      color: #9aa2ba;
    }
  }
}
</style>